import { ComponentPropsWithoutRef, ElementType, ReactNode, useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { useReducedMotion } from '../../hooks/useReducedMotion';

type PanelProps<T extends ElementType> = {
  as?: T;
  children: ReactNode;
  className?: string;
  hoverable?: boolean;
  pulse?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>;

export const HudPanel = <T extends ElementType = 'div'>({
  as,
  children,
  className,
  hoverable = false,
  pulse = false,
  ...rest
}: PanelProps<T>) => {
  const reducedMotion = useReducedMotion();
  const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches;
  const Component = as ?? 'div';
  // Keep a stable motion wrapper so form fields don't remount on every parent rerender.
  const MotionComponent = useMemo(() => motion(Component), [Component]);
  const baseEase: [number, number, number, number] = [0.2, 1, 0.32, 1];

  return (
    <MotionComponent
      initial={mobileViewport ? false : { opacity: 0.88, y: 8 }}
      whileInView={mobileViewport ? undefined : { opacity: 1, y: 0 }}
      viewport={mobileViewport ? undefined : { once: true, amount: 0.3 }}
      animate={
        reducedMotion || !pulse
          ? mobileViewport
            ? { opacity: 1, y: 0 }
            : undefined
          : {
              ...(mobileViewport ? { opacity: 1, y: 0 } : {}),
              boxShadow: [
                '0 20px 70px rgba(0,0,0,0.45)',
                '0 20px 74px rgba(20,60,38,0.48)',
                '0 20px 70px rgba(0,0,0,0.45)'
              ]
            }
      }
      transition={
        reducedMotion || !pulse
          ? {
              opacity: { duration: 0.46, ease: baseEase },
              y: { duration: 0.46, ease: baseEase }
            }
          : {
              opacity: { duration: 0.46, ease: baseEase },
              y: { duration: 0.46, ease: baseEase },
              boxShadow: { duration: 4.4, repeat: Infinity, ease: 'easeInOut' }
            }
      }
      whileHover={
        hoverable
          ? {
              y: -4,
              borderColor: 'rgba(183,255,106,0.7)',
              boxShadow: '0 0 26px rgba(183,255,106,0.18),0 18px 48px rgba(0,0,0,0.45)'
            }
          : undefined
      }
      {...rest}
      className={cn(
        'hud-panel rounded-[1.1rem] border border-brand-lime/20 bg-brand-panel/85 p-4 sm:p-5',
        className
      )}
    >
      {children}
    </MotionComponent>
  );
};
